import React from 'react';
import posthog from 'posthog-js';
import {
    DuplicateEmailError,
    AuthenticationError,
    ApiError,
} from '../types/errors';
import logger from '../utils/logger';

interface Props {
    children: React.ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        // Don't show error UI for expected errors that are handled by components
        if (
            error instanceof DuplicateEmailError ||
            error instanceof AuthenticationError ||
            (error instanceof ApiError && error.errorCode === 'DUPLICATE_EMAIL')
        ) {
            logger.debug(
                'ErrorBoundary: Suppressing expected error:',
                error.message
            );
            return { hasError: false, error: null };
        }

        // For unexpected errors, show error UI
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Log all errors for debugging, but only show UI for unexpected ones
        logger.error('ErrorBoundary caught error', error, {
            componentStack: errorInfo.componentStack,
        });

        // For expected errors, don't report to error tracking services
        if (
            error instanceof DuplicateEmailError ||
            error instanceof AuthenticationError ||
            (error instanceof ApiError && error.errorCode === 'DUPLICATE_EMAIL')
        ) {
            return;
        }

        // For unexpected errors, report to PostHog for error tracking
        // Use posthog.capture with $exception event type (correct API)
        if (
            typeof window !== 'undefined' &&
            posthog &&
            typeof posthog.capture === 'function'
        ) {
            try {
                posthog.capture('$exception', {
                    $exception_type: error.name,
                    $exception_message: error.message,
                    $exception_stack: error.stack,
                    $exception_source: 'ErrorBoundary',
                    // Fingerprint for grouping similar errors
                    $exception_fingerprint: [
                        error.name,
                        error.message.replace(/\d+/g, 'N'), // Normalize numbers for better grouping
                    ],
                    // Additional context
                    react_component_stack: errorInfo.componentStack,
                    app_version:
                        process.env.NEXT_PUBLIC_APP_VERSION || 'unknown',
                    $current_url: window.location.href,
                    $pathname: window.location.pathname,
                });
            } catch (captureError) {
                logger.warn('Failed to capture exception in PostHog');
            }
        }
    }

    render() {
        if (this.state.hasError && this.state.error) {
            // Custom error UI for unexpected errors
            return (
                <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
                    <div className="max-w-md w-full bg-gray-900 rounded-lg p-8 text-center">
                        <h2 className="text-xl font-bold text-red-400 mb-4">
                            Something went wrong
                        </h2>
                        <p className="text-gray-300 mb-6">
                            An unexpected error occurred. Please try refreshing
                            the page.
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/80 transition-colors"
                        >
                            Refresh Page
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
